import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SBINModal from "../components/modals/SBINModal";
import { useAuth } from "../context/AuthContext";
import {
  createInventoryItem,
  deleteInventoryItem,
  getInventory,
  seedStarterInventory,
  updateInventoryItem,
  updateInventoryQuantity,
} from "../api/inventory";

const CATEGORY_OPTIONS = [
  { value: "SUPPLIES", label: "Supplies" },
  { value: "POLISH", label: "Polish" },
  { value: "TOOLS", label: "Tools" },
  { value: "PRODUCTS", label: "Products" },
  { value: "EXTENSIONS", label: "Extensions" },
  { value: "RETAIL", label: "Retail" },
  { value: "OTHER", label: "Other" },
];

const UNIT_OPTIONS = [
  "bottles",
  "jars",
  "packs",
  "pieces",
  "boxes",
  "oz",
  "sets",
  "tubes",
  "rolls",
  "other",
];

const EMPTY_FORM = {
  item_name: "",
  category: "SUPPLIES",
  unit: "bottles",
  quantity: "0",
  low_stock_threshold: "",
  supplier: "",
  cost_per_unit: "",
  inventory_date: "",
};

const EMPTY_QUANTITY_FORM = {
  quantity: "0",
  reason: "RESTOCKED_PURCHASED",
  note: "",
  quantity_date: "",
};

const QUANTITY_REASON_OPTIONS = [
  { value: "RESTOCKED_PURCHASED", label: "Restocked / Purchased" },
  { value: "USED", label: "Used" },
  { value: "DAMAGED_WASTE", label: "Damaged / Waste" },
  { value: "COUNT_ADJUSTMENT", label: "Correction / Count Adjustment" },
];

const pageText = "text-base md:text-lg";
const headerButton =
  "h-10 md:h-11 rounded-full px-5 text-sm md:text-base font-semibold transition";
const inputClass =
  "mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900 focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-100";
const labelClass = "text-sm font-semibold text-slate-700";

const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find((item) => item.value === value)?.label || "Other";

const formatQuantity = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0";
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return `$${num.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const normalizeNumberInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "";
  return Number.isInteger(numberValue)
    ? String(numberValue)
    : numberValue.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

export default function Inventory() {
  const { token } = useAuth();
  const toastRef = useRef(null);
  const [items, setItems] = useState([]);
  const [view, setView] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [salonTimezone, setSalonTimezone] = useState("America/New_York");
  const [salonToday, setSalonToday] = useState("");
  const [canCreate, setCanCreate] = useState(false);
  const [canSeedStarter, setCanSeedStarter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [quantityTarget, setQuantityTarget] = useState(null);
  const [quantityForm, setQuantityForm] = useState(EMPTY_QUANTITY_FORM);
  const [quantityError, setQuantityError] = useState("");
  const [quantitySaving, setQuantitySaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [starterSaving, setStarterSaving] = useState(false);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getInventory({}, token);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSalonTimezone(data?.salon_timezone || "America/New_York");
      setSalonToday(data?.salon_today || "");
      setCanCreate(Boolean(data?.can_create));
      setCanSeedStarter(Boolean(data?.can_seed_starter));
    } catch (err) {
      setError("Inventory could not be loaded. Please try again.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  useEffect(
    () => () => {
      if (toastRef.current) clearTimeout(toastRef.current);
    },
    []
  );

  const showToast = useCallback((message) => {
    setToast(message);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => {
      setToast("");
      toastRef.current = null;
    }, 3000);
  }, []);

  const lowStockCount = useMemo(
    () => items.filter((item) => item.is_low_stock).length,
    [items]
  );

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (view === "low_stock" && !item.is_low_stock) return false;
        if (categoryFilter && item.category !== categoryFilter) return false;
        return true;
      }),
    [categoryFilter, items, view]
  );

  const categoryCounts = useMemo(() => {
    const counts = new Map();
    items.forEach((item) => {
      counts.set(item.category, (counts.get(item.category) || 0) + 1);
    });
    return counts;
  }, [items]);

  const openAddModal = () => {
    setEditingItem(null);
    setForm({
      ...EMPTY_FORM,
      inventory_date: salonToday || "",
    });
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      item_name: item.item_name || "",
      category: item.category || "SUPPLIES",
      unit: item.unit || "bottles",
      quantity: normalizeNumberInput(item.quantity),
      low_stock_threshold: normalizeNumberInput(item.low_stock_threshold),
      supplier: item.supplier || "",
      cost_per_unit: normalizeNumberInput(item.cost_per_unit),
      inventory_date: item.inventory_date_local || salonToday || "",
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingItem(null);
    setFormError("");
  };

  const validateForm = () => {
    if (!form.item_name.trim()) return "Item name is required.";
    if (!form.category) return "Category is required.";
    if (!form.unit.trim()) return "Unit is required.";
    if (form.quantity === "" || Number(form.quantity) < 0) return "Quantity is required.";
    return "";
  };

  const buildPayload = () => ({
    item_name: form.item_name.trim(),
    category: form.category,
    unit: form.unit.trim(),
    quantity: Number(form.quantity || 0),
    low_stock_threshold:
      form.low_stock_threshold === "" ? null : Number(form.low_stock_threshold),
    supplier: form.supplier.trim() || null,
    cost_per_unit: form.cost_per_unit === "" ? null : Number(form.cost_per_unit),
    inventory_date: form.inventory_date || null,
  });

  const handleSave = async () => {
    const validation = validateForm();
    if (validation) {
      setFormError(validation);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (editingItem) {
        await updateInventoryItem(editingItem.id, buildPayload(), token);
        showToast("Inventory item updated.");
      } else {
        await createInventoryItem(buildPayload(), token);
        showToast("Inventory item added.");
      }
      setModalOpen(false);
      setEditingItem(null);
      await loadInventory();
    } catch (err) {
      setFormError("Inventory item could not be saved. Please check the fields and try again.");
    } finally {
      setSaving(false);
    }
  };

  const openQuantityModal = (item) => {
    setQuantityTarget(item);
    setQuantityForm({
      quantity: normalizeNumberInput(item.quantity),
      reason: "RESTOCKED_PURCHASED",
      note: "",
      quantity_date: salonToday || item.last_quantity_update_date_local || "",
    });
    setQuantityError("");
  };

  const closeQuantityModal = () => {
    if (quantitySaving) return;
    setQuantityTarget(null);
    setQuantityError("");
  };

  const handleQuantitySave = async () => {
    if (quantityForm.quantity === "" || Number(quantityForm.quantity) < 0) {
      setQuantityError("New quantity is required.");
      return;
    }
    setQuantitySaving(true);
    setQuantityError("");
    try {
      await updateInventoryQuantity(
        quantityTarget.id,
        {
          quantity: Number(quantityForm.quantity || 0),
          reason: quantityForm.reason || "COUNT_ADJUSTMENT",
          note: quantityForm.note.trim() || null,
          quantity_date: quantityForm.quantity_date || null,
        },
        token
      );
      setQuantityTarget(null);
      showToast("Quantity updated.");
      await loadInventory();
    } catch (err) {
      setQuantityError("Quantity could not be updated. Please try again.");
    } finally {
      setQuantitySaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    try {
      await deleteInventoryItem(deleteTarget.id, token);
      setDeleteTarget(null);
      showToast("Inventory item deleted.");
      await loadInventory();
    } catch (err) {
      showToast("Inventory item could not be deleted.");
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleSeedStarter = async () => {
    setStarterSaving(true);
    try {
      const data = await seedStarterInventory(token);
      showToast(`${data?.inserted_count || 30} starter items added.`);
      await loadInventory();
    } catch (err) {
      showToast("Starter inventory could not be added.");
    } finally {
      setStarterSaving(false);
    }
  };

  const renderItemRow = (item) => {
    const stockPercent = Math.max(3, Math.min(100, Number(item.stock_percent || 0)));
    const isLow = Boolean(item.is_low_stock);
    return (
      <div key={item.id} className="border-t border-slate-100 px-4 py-4">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-bold text-slate-900">{item.item_name}</h2>
              {isLow ? (
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-300 bg-amber-50 text-xs font-bold text-amber-700"
                  title="Low stock"
                >
                  !
                </span>
              ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span>{categoryLabel(item.category)}</span>
              {item.supplier ? <span>- {item.supplier}</span> : null}
              {item.cost_per_unit ? <span>- {formatMoney(item.cost_per_unit)} each</span> : null}
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full transition-all ${
                  isLow ? "bg-amber-500" : "bg-rose-500"
                }`}
                style={{ width: `${stockPercent}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-slate-500">
              Last updated {formatDate(item.last_quantity_update_date_local)}
            </div>
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <div className={isLow ? "text-amber-700" : "text-slate-900"}>
              <div className="text-base font-bold">
                {formatQuantity(item.quantity)} {item.unit}
              </div>
              <div className="text-right text-xs text-slate-500">
                min: {item.low_stock_threshold ? formatQuantity(item.low_stock_threshold) : "-"}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {item.can_update_quantity ? (
                <button
                  type="button"
                  onClick={() => openQuantityModal(item)}
                  className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Update Qty
                </button>
              ) : null}
              {item.can_edit ? (
                <button
                  type="button"
                  onClick={() => openEditModal(item)}
                  className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Edit
                </button>
              ) : null}
              {item.can_delete ? (
                <button
                  type="button"
                  onClick={() => setDeleteTarget(item)}
                  className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                >
                  Delete
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
        <div
          className={`relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl ${pageText}`}
        >
          <div className="flex flex-col gap-3 bg-rose-500 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <h1 className="text-xl font-semibold text-white md:text-2xl">Inventory</h1>
            {canCreate ? (
              <button
                type="button"
                onClick={openAddModal}
                className={`${headerButton} bg-white text-rose-600 hover:bg-rose-50`}
              >
                Add Item
              </button>
            ) : null}
          </div>

          <div className="space-y-5 px-6 py-6">
            <p className="text-sm text-slate-600">
              Salon time: {salonTimezone}
            </p>

            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setView("all")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    view === "all"
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  All ({items.length})
                </button>
                <button
                  type="button"
                  onClick={() => setView("low_stock")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    view === "low_stock"
                      ? "bg-slate-900 text-white"
                      : "border border-amber-200 bg-white text-amber-700 hover:bg-amber-50"
                  }`}
                >
                  Low Stock ({lowStockCount})
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Category</span>
                <select
                  value={categoryFilter}
                  onChange={(event) => setCategoryFilter(event.target.value)}
                  className="h-10 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700"
                >
                  <option value="">All categories</option>
                  {CATEGORY_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                      {categoryCounts.get(item.value) ? ` (${categoryCounts.get(item.value)})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {error}
              </div>
            ) : null}

            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm font-semibold text-slate-500 shadow-sm">
                Loading inventory...
              </div>
            ) : filteredItems.length ? (
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                {filteredItems.map(renderItemRow)}
              </section>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <h2 className="text-lg font-bold text-slate-900">
                  {items.length ? "No items match this filter" : "No inventory items yet"}
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  {items.length
                    ? "Try All or choose another category."
                    : "Add an item to start tracking salon supplies and low stock."}
                </p>
                {!items.length && canSeedStarter ? (
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      onClick={handleSeedStarter}
                      disabled={starterSaving}
                      className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {starterSaving ? "Adding..." : "Add Starter Inventory"}
                    </button>
                    <button
                      type="button"
                      onClick={openAddModal}
                      className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Add One Item
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-[12000] -translate-x-1/2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <SBINModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingItem ? "Edit Inventory Item" : "Add Inventory Item"}
        size="lg"
        fitContent
        closeOnBackdrop
        closeOnEscape
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {formError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {formError}
            </div>
          ) : null}
          <div>
            <label className={labelClass}>Item Name</label>
            <input
              value={form.item_name}
              onChange={(event) =>
                setForm((current) => ({ ...current, item_name: event.target.value }))
              }
              className={inputClass}
              placeholder="Acetone (36oz)"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Category</label>
              <select
                value={form.category}
                onChange={(event) =>
                  setForm((current) => ({ ...current, category: event.target.value }))
                }
                className={inputClass}
              >
                {CATEGORY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Unit</label>
              <select
                value={form.unit}
                onChange={(event) =>
                  setForm((current) => ({ ...current, unit: event.target.value }))
                }
                className={inputClass}
              >
                {UNIT_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Quantity</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.quantity}
                onChange={(event) =>
                  setForm((current) => ({ ...current, quantity: event.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Low-stock threshold</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.low_stock_threshold}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    low_stock_threshold: event.target.value,
                  }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Supplier</label>
              <input
                value={form.supplier}
                onChange={(event) =>
                  setForm((current) => ({ ...current, supplier: event.target.value }))
                }
                className={inputClass}
                placeholder="Beauty Supply Co."
              />
            </div>
            <div>
              <label className={labelClass}>Cost per unit</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.cost_per_unit}
                onChange={(event) =>
                  setForm((current) => ({ ...current, cost_per_unit: event.target.value }))
                }
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Inventory date</label>
            <input
              type="date"
              value={form.inventory_date}
              onChange={(event) =>
                setForm((current) => ({ ...current, inventory_date: event.target.value }))
              }
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              Date is based on salon time: {salonTimezone}
            </p>
          </div>
        </div>
      </SBINModal>

      <SBINModal
        isOpen={Boolean(quantityTarget)}
        onClose={closeQuantityModal}
        title="Update Quantity"
        size="sm"
        fitContent
        closeOnBackdrop
        closeOnEscape
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeQuantityModal}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleQuantitySave}
              disabled={quantitySaving}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {quantitySaving ? "Saving..." : "Save"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {quantityError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {quantityError}
            </div>
          ) : null}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            Current quantity:{" "}
            <span className="font-semibold">
              {formatQuantity(quantityTarget?.quantity)} {quantityTarget?.unit}
            </span>
          </div>
          <div>
            <label className={labelClass}>New quantity</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={quantityForm.quantity}
              onChange={(event) =>
                setQuantityForm((current) => ({ ...current, quantity: event.target.value }))
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Reason</label>
            <select
              value={quantityForm.reason}
              onChange={(event) =>
                setQuantityForm((current) => ({ ...current, reason: event.target.value }))
              }
              className={inputClass}
            >
              {QUANTITY_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Note</label>
            <input
              value={quantityForm.note}
              onChange={(event) =>
                setQuantityForm((current) => ({ ...current, note: event.target.value }))
              }
              className={inputClass}
              placeholder="Restocked, Used, Damaged, Correction"
            />
          </div>
          <div>
            <label className={labelClass}>Quantity update date</label>
            <input
              type="date"
              value={quantityForm.quantity_date}
              onChange={(event) =>
                setQuantityForm((current) => ({
                  ...current,
                  quantity_date: event.target.value,
                }))
              }
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              Date is based on salon time: {salonTimezone}
            </p>
          </div>
        </div>
      </SBINModal>

      <SBINModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete Inventory Item"
        size="sm"
        fitContent
        closeOnBackdrop
        closeOnEscape
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteSaving}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleteSaving ? "Deleting..." : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          Delete <span className="font-semibold">{deleteTarget?.item_name}</span> from inventory?
        </p>
      </SBINModal>
    </div>
  );
}
